import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { Outlet } from 'react-router';

import { AppRoot, ThemeProvider } from '@change/ds-core-ui';

import { createNavigationUtils } from '@change-corgi/core-react/navigation';
import { AppRenderInfoProvider } from '@change-corgi/core-react/ssr/render';
import { UtilityContextProvider } from '@change-corgi/core-react/utilityContext';

import { ErrorPage } from '#src/app/app/error';
import { DefaultLayout, SkipToMainContent } from '#src/app/app/layout';
import { L10nProvider } from '#src/app/shared/hooks/l10n';
import { SessionProvider } from '#src/app/shared/hooks/session';
import { SessionConfigurationProvider } from '#src/app/shared/hooks/sessionConfiguration';
import { UserAgentContextProvider } from '#src/app/shared/hooks/userAgent';
import { useWebappTrackLoad } from '#src/app/shared/hooks/webapp';

import { AppGlobalStyle } from './GlobalStyle';
import type { AppOptions } from './types';

const fullyExternalNavigation = createNavigationUtils({ internalRoutes: [] });

export const ErrorInnerApp: React.FC<AppOptions & { hasTranslations: boolean }> = ({
	hasTranslations,
	errorStatus,
	errorId,
}) => {
	const ErrorWithId = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/no-shadow
			function ErrorWithId() {
				return <ErrorPage status={errorStatus || 500} errorId={errorId} suppressHydrationWarning />;
			},
		[errorStatus, errorId],
	);

	return (
		<>
			{hasTranslations ? (
				<>
					<SkipToMainContent />
					<DefaultLayout suppressHydrationWarning>
						{/* eslint-disable-next-line react-hooks/static-components */}
						<ErrorWithId />
					</DefaultLayout>
				</>
			) : (
				// eslint-disable-next-line react-hooks/static-components
				<ErrorWithId />
			)}
		</>
	);
};

export function ErrorApp(appOptions: PropsWithChildren<AppOptions & { hasTranslations: boolean }>): JSX.Element | null {
	const {
		utilities,
		l10n: { locale, countryCode },
		session,
		sessionConfiguration,
		sessionAvailableAtFirstRender,
		children,
		bot,
	} = appOptions;

	const utilitiesWithErrorPageNavigation = useMemo(
		() => ({ ...utilities, navigation: fullyExternalNavigation }),
		[utilities],
	);

	useWebappTrackLoad(appOptions);

	return (
		<ThemeProvider>
			<AppRoot>
				<AppGlobalStyle />
				<AppRenderInfoProvider>
					<UtilityContextProvider utilities={utilitiesWithErrorPageNavigation}>
						<L10nProvider locale={locale} countryCode={countryCode}>
							<UserAgentContextProvider bot={bot}>
								<SessionConfigurationProvider value={sessionConfiguration}>
									<SessionProvider session={session} sessionAvailableAtFirstRender={sessionAvailableAtFirstRender}>
										{children || <Outlet />}
									</SessionProvider>
								</SessionConfigurationProvider>
							</UserAgentContextProvider>
						</L10nProvider>
					</UtilityContextProvider>
				</AppRenderInfoProvider>
			</AppRoot>
		</ThemeProvider>
	);
}
